import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
  MdArrowBack, MdVisibility, MdThumbUp, MdThumbDown, MdComment,
  MdSearch, MdCalendarToday, MdRefresh, MdDelete, MdPerson,
  MdPhone, MdDevices, MdTrendingUp, MdLocationOn, MdFileDownload,
  MdAndroid, MdPhoneIphone, MdComputer, MdCheckCircle, MdShare
} from 'react-icons/md';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import { formatDate } from '../utils/format';

export default function ArticleEngagementDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') || 'overview';
  const [loading, setLoading] = useState(true);
  const [header, setHeader] = useState(null);
  const [data, setData] = useState(null);

  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { page, limit, setPage, setLimit } = usePagination(15);

  const setTab = (tabName) => {
    setSearchParams({ tab: tabName });
    setPage(1);
  };

  const fetchEngagement = useCallback(async () => {
    setLoading(true);
    try {
      const params = { tab: activeTab, page, limit };
      if (search.trim()) params.search = search.trim();
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const res = await api.get(`/admin/articles/${id}/engagement`, { params });
      if (res.data.success) {
        setHeader(res.data.header);
        setData(res.data.data);
      }
    } catch (err) {
      toast.error('Failed to load article engagement details');
    } finally {
      setLoading(false);
    }
  }, [id, activeTab, page, limit, search, startDate, endDate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEngagement();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchEngagement]);

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    try {
      await api.delete(`/admin/comments/${commentId}`);
      toast.success('Comment deleted successfully');
      fetchEngagement();
    } catch (err) {
      toast.error('Failed to delete comment');
    }
  };

  const resetFilters = () => {
    setSearch('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const exportCSV = () => {
    if (!data?.rows || data.rows.length === 0) {
      return toast.error('No data available to export');
    }
    const keys = Object.keys(data.rows[0]);
    const csvContent = 'data:text/csv;charset=utf-8,' +
      [keys.join(','), ...data.rows.map(row => keys.map(k => `"${(row[k] || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `article_${id}_${activeTab}_engagement.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${activeTab} data to CSV`);
  };

  const getPlatformIcon = (platform) => {
    const p = (platform || '').toLowerCase();
    if (p.includes('ios') || p.includes('apple')) return <MdPhoneIphone style={{ color: 'var(--text)' }} />;
    if (p.includes('web') || p.includes('chrome')) return <MdComputer style={{ color: '#2563EB' }} />;
    return <MdAndroid style={{ color: '#16A34A' }} />;
  };

  // Timeline SVG calculations
  const viewsCount = header?.stats.views || 0;
  const likesCount = header?.stats.likes || 0;
  const commentsCount = header?.stats.comments || 0;
  const dislikesCount = header?.stats.dislikes || 0;
  const sharesCount = header?.stats.shares || 0;
  const totalEvents = viewsCount + likesCount + commentsCount + dislikesCount + sharesCount;
  const divisor = totalEvents > 0 ? totalEvents : 1;

  const viewsPct = totalEvents > 0 ? ((viewsCount / divisor) * 100).toFixed(1) : '0';
  const likesPct = totalEvents > 0 ? ((likesCount / divisor) * 100).toFixed(1) : '0';
  const commentsPct = totalEvents > 0 ? ((commentsCount / divisor) * 100).toFixed(1) : '0';
  const dislikesPct = totalEvents > 0 ? ((dislikesCount / divisor) * 100).toFixed(1) : '0';
  const sharesPct = totalEvents > 0 ? ((sharesCount / divisor) * 100).toFixed(1) : '0';

  return (
    <div className="page" style={{ padding: '24px 32px', maxWidth: '100%', margin: '0 auto' }}>
      {/* Top Header Bar & Breadcrumbs */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 16 }}>
        <button
          className="btn btn-ghost"
          onClick={() => navigate('/articles')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, borderColor: 'var(--border)', padding: '8px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}
        >
          <MdArrowBack size={18} /> Back to Articles
        </button>

        <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Dashboard</Link>
          <span>›</span>
          <Link to="/articles" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Articles</Link>
          <span>›</span>
          <span style={{ color: '#DC2626', fontWeight: 600 }}>Article Engagement</span>
        </div>
      </div>

      {/* Main Article Banner Header Card */}
      {header && (
        <div style={{
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
          borderRadius: 16,
          padding: '24px 28px',
          color: '#FFFFFF',
          boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.15)',
          marginBottom: 24
        }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', flex: 1, minWidth: 300 }}>
              {header.image_url || header.thumbnail_url ? (
                <img
                  src={header.thumbnail_url || header.image_url}
                  alt=""
                  style={{ width: 140, height: 95, objectFit: 'cover', borderRadius: 12, border: '2px solid rgba(255,255,255,0.1)', flexShrink: 0 }}
                />
              ) : (
                <div style={{ width: 140, height: 95, background: '#334155', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', flexShrink: 0 }}>
                  No Image
                </div>
              )}

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <span className="badge" style={{ background: '#DC2626', color: '#FFF', fontWeight: 700, padding: '4px 10px', fontSize: 11 }}>
                    {header.category || 'General'}
                  </span>
                  {header.city && (
                    <span style={{ fontSize: 12, color: '#CBD5E1', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MdLocationOn size={14} /> {header.city}
                    </span>
                  )}
                  <span className={`badge badge-${header.status === 'published' ? 'published' : 'draft'}`} style={{ textTransform: 'capitalize' }}>
                    {header.status}
                  </span>
                </div>

                <h3 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 700, color: '#F8FAFC', lineHeight: 1.3 }}>
                  {header.title}
                </h3>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 8 }}>
                  <div>By <strong>{header.reporter_name}</strong></div>
                  <div>Published: <strong>{header.published_at ? formatDate(header.published_at) : 'N/A'}</strong></div>
                </div>

                {header.summary && (
                  <p style={{ margin: 0, fontSize: 13, color: '#CBD5E1', lineHeight: 1.4, opacity: 0.95, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {header.summary}
                  </p>
                )}
              </div>
            </div>

            {/* Header 4 Quick Metric Dark Cards */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div
                onClick={() => setTab('views')}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 12, padding: '14px 20px', cursor: 'pointer', textAlign: 'left', minWidth: 105
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <MdVisibility size={14} /> Views
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#F8FAFC', marginTop: 4 }}>{viewsCount.toLocaleString()}</div>
              </div>

              <div
                onClick={() => setTab('likes')}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 12, padding: '14px 20px', cursor: 'pointer', textAlign: 'left', minWidth: 105
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <MdThumbUp size={14} color="#EF4444" /> Likes
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#F8FAFC', marginTop: 4 }}>{likesCount.toLocaleString()}</div>
              </div>

              <div
                onClick={() => setTab('dislikes')}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 12, padding: '14px 20px', cursor: 'pointer', textAlign: 'left', minWidth: 105
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <MdThumbDown size={14} color="#F97316" /> Dislikes
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#F8FAFC', marginTop: 4 }}>{dislikesCount.toLocaleString()}</div>
              </div>

              <div
                onClick={() => setTab('comments')}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 12, padding: '14px 20px', cursor: 'pointer', textAlign: 'left', minWidth: 105
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <MdComment size={14} color="#A855F7" /> Comments
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#F8FAFC', marginTop: 4 }}>{commentsCount.toLocaleString()}</div>
              </div>

              <div
                onClick={() => setTab('shares')}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 12, padding: '14px 20px', cursor: 'pointer', textAlign: 'left', minWidth: 105
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <MdShare size={14} color="#0EA5E9" /> Shares
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#F8FAFC', marginTop: 4 }}>{sharesCount.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs Bar with Red Active Underline */}
      <div style={{ display: 'flex', gap: 12, borderBottom: '2px solid #E2E8F0', marginBottom: 24 }}>
        {[
          { key: 'overview', label: 'Overview', icon: <MdTrendingUp /> },
          { key: 'likes', label: `Likes (${likesCount})`, icon: <MdThumbUp /> },
          { key: 'dislikes', label: `Dislikes (${dislikesCount})`, icon: <MdThumbDown /> },
          { key: 'comments', label: `Comments (${commentsCount})`, icon: <MdComment /> },
          { key: 'views', label: `Views (${viewsCount})`, icon: <MdVisibility /> },
          { key: 'shares', label: `Shares (${sharesCount})`, icon: <MdShare /> },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '12px 22px',
              fontSize: 14,
              fontWeight: activeTab === t.key ? 700 : 500,
              color: activeTab === t.key ? '#DC2626' : '#64748B',
              borderBottom: activeTab === t.key ? '3px solid #DC2626' : '3px solid transparent',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transition: 'all 0.2s',
              marginBottom: -2
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Filter & Export Bar for Data Tabs */}
      {activeTab !== 'overview' && (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 24, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
            <div style={{ position: 'relative', minWidth: 260, flex: 1 }}>
              <MdSearch style={{ position: 'absolute', left: 14, top: 12, color: 'var(--text-secondary)' }} size={18} />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ paddingLeft: 42, width: '100%', height: 42, borderRadius: 8, border: '1px solid var(--border)', fontSize: 13.5 }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MdCalendarToday color="#64748B" />
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                style={{ height: 42, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
              />
              <span style={{ color: 'var(--text-secondary)' }}>to</span>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                style={{ height: 42, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
              />
            </div>

            {(search || startDate || endDate) && (
              <button className="btn btn-ghost" onClick={resetFilters} style={{ height: 42, display: 'flex', alignItems: 'center', gap: 6 }}>
                <MdRefresh /> Reset
              </button>
            )}
          </div>

          <button
            className="btn btn-ghost"
            onClick={exportCSV}
            style={{ height: 42, borderColor: '#DC2626', color: '#DC2626', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <MdFileDownload size={18} /> Export CSV
          </button>
        </div>
      )}

      {/* Main Content Sections */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-secondary)' }}>Loading engagement data...</div>
      ) : (
        <>
          {/* TAB 1: OVERVIEW ANALYTICS DASHBOARD */}
          {activeTab === 'overview' && (
            <div>
              {/* 5 KPI Stat Cards Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 16, marginBottom: 24 }}>
                {/* Views KPI */}
                <div className="card" style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3B82F6', flexShrink: 0 }}>
                    <MdVisibility size={24} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Total Views</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', margin: '2px 0' }}>{viewsCount.toLocaleString()}</div>
                    {viewsCount > 0 && data?.summary?.views_growth && (
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: '#16A34A' }}>↗ {data.summary.views_growth}% from last 7 days</div>
                    )}
                  </div>
                </div>

                {/* Likes KPI */}
                <div className="card" style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EF4444', flexShrink: 0 }}>
                    <MdThumbUp size={24} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Total Likes</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', margin: '2px 0' }}>{likesCount.toLocaleString()}</div>
                    {likesCount > 0 && data?.summary?.likes_growth && (
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: '#16A34A' }}>↗ {data.summary.likes_growth}% from last 7 days</div>
                    )}
                  </div>
                </div>

                {/* Dislikes KPI */}
                <div className="card" style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: '#FFF7ED', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F97316', flexShrink: 0 }}>
                    <MdThumbDown size={24} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Total Dislikes</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', margin: '2px 0' }}>{dislikesCount.toLocaleString()}</div>
                    {dislikesCount > 0 && data?.summary?.dislikes_growth && (
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: '#DC2626' }}>↘ {data.summary.dislikes_growth}% from last 7 days</div>
                    )}
                  </div>
                </div>

                {/* Comments KPI */}
                <div className="card" style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: '#F3E8FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#A855F7', flexShrink: 0 }}>
                    <MdComment size={24} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Total Comments</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', margin: '2px 0' }}>{commentsCount.toLocaleString()}</div>
                    {commentsCount > 0 && data?.summary?.comments_growth && (
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: '#16A34A' }}>↗ {data.summary.comments_growth}% from last 7 days</div>
                    )}
                  </div>
                </div>

                {/* Shares KPI */}
                <div className="card" style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0EA5E9', flexShrink: 0 }}>
                    <MdShare size={24} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Total Shares</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', margin: '2px 0' }}>{sharesCount.toLocaleString()}</div>
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: '#0EA5E9' }}>Unique member shares</div>
                  </div>
                </div>

                {/* Approval Rating KPI */}
                <div className="card" style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8B5CF6', flexShrink: 0 }}>
                    <MdTrendingUp size={24} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Approval Rating</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: '#8B5CF6', margin: '2px 0' }}>
                      {dislikesCount === 0 ? '100%' : `${data?.summary?.approval_rating ?? Math.round((likesCount / (likesCount + dislikesCount)) * 100)}%`}
                    </div>
                    {(likesCount + dislikesCount) > 0 && data?.summary?.rating_growth && (
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: '#16A34A' }}>↗ {data.summary.rating_growth}% from last 7 days</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Engagement Visual Analytics Charts Grid (2 Cards) */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 24 }} className="status-grid">
                {/* Left Chart: Engagement Overview */}
                <div className="card" style={{ padding: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Engagement Overview</h4>
                    {totalEvents > 0 && (
                      <div style={{ display: 'flex', gap: 14, fontSize: 12, fontWeight: 600 }}>
                        <span style={{ color: '#3B82F6', display: 'flex', alignItems: 'center', gap: 4 }}>🔵 Views</span>
                        <span style={{ color: '#EF4444', display: 'flex', alignItems: 'center', gap: 4 }}>🔴 Likes</span>
                        <span style={{ color: '#A855F7', display: 'flex', alignItems: 'center', gap: 4 }}>🟣 Comments</span>
                        <span style={{ color: '#F97316', display: 'flex', alignItems: 'center', gap: 4 }}>🟠 Dislikes</span>
                        <span style={{ color: '#0EA5E9', display: 'flex', alignItems: 'center', gap: 4 }}>🌐 Shares</span>
                      </div>
                    )}
                  </div>

                  {totalEvents > 0 ? (() => {
                    const timelineList = data?.timeline || [];
                    const maxVal = Math.max(5, ...timelineList.map(t => Math.max(t.views || 0, t.likes || 0, t.comments || 0, t.dislikes || 0, t.shares || 0)));
                    const yTicks = [
                      { val: maxVal, y: 20 },
                      { val: Math.round(maxVal * 0.66), y: 65 },
                      { val: Math.round(maxVal * 0.33), y: 110 },
                      { val: 0, y: 155 }
                    ];

                    const viewsPoints = timelineList.map((t, idx) => `${65 + idx * 80},${155 - ((t.views || 0) / maxVal) * 135}`).join(' ');
                    const likesPoints = timelineList.map((t, idx) => `${65 + idx * 80},${155 - ((t.likes || 0) / maxVal) * 135}`).join(' ');
                    const commentsPoints = timelineList.map((t, idx) => `${65 + idx * 80},${155 - ((t.comments || 0) / maxVal) * 135}`).join(' ');
                    const dislikesPoints = timelineList.map((t, idx) => `${65 + idx * 80},${155 - ((t.dislikes || 0) / maxVal) * 135}`).join(' ');
                    const sharesPoints = timelineList.map((t, idx) => `${65 + idx * 80},${155 - ((t.shares || 0) / maxVal) * 135}`).join(' ');

                    return (
                      /* SVG Dynamic Multi-line Chart with Y-Axis */
                      <div style={{ width: '100%', height: 210, position: 'relative' }}>
                        <svg viewBox="0 0 600 200" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                          {/* Horizontal Grid Lines */}
                          {yTicks.map((tick, idx) => (
                            <line key={idx} x1="45" y1={tick.y} x2="575" y2={tick.y} stroke="#F1F5F9" strokeWidth="1" />
                          ))}

                          {/* Vertical Y-Axis Line */}
                          <line x1="45" y1="15" x2="45" y2="155" stroke="#CBD5E1" strokeWidth="1.5" />

                          {/* Left Y-Axis Numerical Labels */}
                          {yTicks.map((tick, idx) => (
                            <text key={idx} x="36" y={tick.y + 4} fontSize="11" fill="#64748B" textAnchor="end" fontWeight="600">
                              {tick.val}
                            </text>
                          ))}

                          {/* Dynamic Views Line (Blue) */}
                          <polyline fill="none" stroke="#3B82F6" strokeWidth="3" points={viewsPoints} />
                          {/* Dynamic Likes Line (Red) */}
                          <polyline fill="none" stroke="#EF4444" strokeWidth="2.5" points={likesPoints} />
                          {/* Dynamic Comments Line (Purple) */}
                          <polyline fill="none" stroke="#A855F7" strokeWidth="2" points={commentsPoints} />
                          {/* Dynamic Dislikes Line (Orange) */}
                          <polyline fill="none" stroke="#F97316" strokeWidth="2" points={dislikesPoints} />
                          {/* Dynamic Shares Line (Cyan) */}
                          <polyline fill="none" stroke="#0EA5E9" strokeWidth="2" points={sharesPoints} />

                          {/* Dynamic Data Point Circles */}
                          {timelineList.map((t, idx) => {
                            const cx = 65 + idx * 80;
                            const cyV = 155 - ((t.views || 0) / maxVal) * 135;
                            const cyL = 155 - ((t.likes || 0) / maxVal) * 135;
                            const cyC = 155 - ((t.comments || 0) / maxVal) * 135;
                            const cyD = 155 - ((t.dislikes || 0) / maxVal) * 135;
                            const cyS = 155 - ((t.shares || 0) / maxVal) * 135;
                            return (
                              <g key={idx}>
                                {t.views > 0 && <circle cx={cx} cy={cyV} r="3.5" fill="#3B82F6" />}
                                {t.likes > 0 && <circle cx={cx} cy={cyL} r="3.5" fill="#EF4444" />}
                                {t.comments > 0 && <circle cx={cx} cy={cyC} r="3.5" fill="#A855F7" />}
                                {t.dislikes > 0 && <circle cx={cx} cy={cyD} r="3.5" fill="#F97316" />}
                                {t.shares > 0 && <circle cx={cx} cy={cyS} r="3.5" fill="#0EA5E9" />}
                              </g>
                            );
                          })}

                          {/* X-Axis Baseline Line */}
                          <line x1="45" y1="155" x2="575" y2="155" stroke="#CBD5E1" strokeWidth="1.5" />

                          {/* X-Axis Date Labels */}
                          {timelineList.map((t, idx) => (
                            <text key={idx} x={65 + idx * 80} y="175" fontSize="11" fill="#64748B" textAnchor="middle" fontWeight="600">
                              {t.label}
                            </text>
                          ))}
                        </svg>
                      </div>
                    );
                  })() : (
                    /* Zero Data Empty State for Overview Chart */
                    <div style={{ textAlign: 'center', padding: '45px 20px', background: 'var(--bg)', borderRadius: 12, border: '1px dashed #E2E8F0' }}>
                      <MdTrendingUp size={44} color="#94A3B8" style={{ marginBottom: 8 }} />
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>No engagement data available</div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Not enough historical data to generate the engagement trend.</div>
                    </div>
                  )}
                </div>

                {/* Right Chart: Engagement Summary (Doughnut/Ring Chart) */}
                <div className="card" style={{ padding: 24, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Engagement Summary</h4>

                  {totalEvents > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20, justifyContent: 'center' }}>
                      {/* Ring SVG */}
                      <div style={{ position: 'relative', width: 130, height: 130, flexShrink: 0 }}>
                        <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                          <path stroke="#3B82F6" strokeWidth="4.5" fill="none" strokeDasharray={`${viewsPct} 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                          <path stroke="#EF4444" strokeWidth="4.5" fill="none" strokeDasharray={`${likesPct} 100`} strokeDashoffset={`-${viewsPct}`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                          <path stroke="#A855F7" strokeWidth="4.5" fill="none" strokeDasharray={`${commentsPct} 100`} strokeDashoffset={`-${Number(viewsPct) + Number(likesPct)}`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                          <path stroke="#F97316" strokeWidth="4.5" fill="none" strokeDasharray={`${dislikesPct} 100`} strokeDashoffset={`-${Number(viewsPct) + Number(likesPct) + Number(commentsPct)}`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                          <path stroke="#0EA5E9" strokeWidth="4.5" fill="none" strokeDasharray={`${sharesPct} 100`} strokeDashoffset={`-${Number(viewsPct) + Number(likesPct) + Number(commentsPct) + Number(dislikesPct)}`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                        </svg>
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', lineHeight: 1 }}>
                            {totalEvents.toLocaleString()}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2, fontWeight: 600 }}>Total Events</div>
                        </div>
                      </div>

                      {/* Percentage Breakdown Legend */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#3B82F6', display: 'inline-block' }} />
                          <span style={{ color: 'var(--text-secondary)', minWidth: 60 }}>Views</span>
                          <strong style={{ color: 'var(--text)' }}>{viewsCount.toLocaleString()} ({viewsPct}%)</strong>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#EF4444', display: 'inline-block' }} />
                          <span style={{ color: 'var(--text-secondary)', minWidth: 60 }}>Likes</span>
                          <strong style={{ color: 'var(--text)' }}>{likesCount.toLocaleString()} ({likesPct}%)</strong>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#A855F7', display: 'inline-block' }} />
                          <span style={{ color: 'var(--text-secondary)', minWidth: 60 }}>Comments</span>
                          <strong style={{ color: 'var(--text)' }}>{commentsCount.toLocaleString()} ({commentsPct}%)</strong>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#F97316', display: 'inline-block' }} />
                          <span style={{ color: 'var(--text-secondary)', minWidth: 60 }}>Dislikes</span>
                          <strong style={{ color: 'var(--text)' }}>{dislikesCount.toLocaleString()} ({dislikesPct}%)</strong>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#0EA5E9', display: 'inline-block' }} />
                          <span style={{ color: 'var(--text-secondary)', minWidth: 60 }}>Shares</span>
                          <strong style={{ color: 'var(--text)' }}>{sharesCount.toLocaleString()} ({sharesPct}%)</strong>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Zero Data Empty State for Summary Chart */
                    <div style={{ textAlign: 'center', padding: '45px 20px', background: 'var(--bg)', borderRadius: 12, border: '1px dashed #E2E8F0', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>No engagement summary available</div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>0 Total Events recorded</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Engagement Activity Stream Table */}
              <div className="card" style={{ overflow: 'hidden' }}>
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E8F0' }}>
                  <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Recent Engagement Activity</h4>
                </div>

                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Platform</th>
                        <th>User / Guest</th>
                        <th>Activity Type</th>
                        <th>Details</th>
                        <th>Date & Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.recent_activity?.map((act, idx) => (
                        <tr key={idx}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>
                              {getPlatformIcon(act.platform)}
                              <span>{act.platform || 'Android'}</span>
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                                <MdPerson />
                              </div>
                              <span style={{ fontWeight: 600, color: 'var(--text)' }}>{act.user_name}</span>
                            </div>
                          </td>
                          <td>
                            <span className="badge" style={{
                              background: act.type === 'View' ? '#EFF6FF' : act.type === 'Like' ? '#FEF2F2' : act.type === 'Comment' ? '#F3E8FF' : act.type === 'Share' ? '#F0F9FF' : '#FFF7ED',
                              color: act.type === 'View' ? '#2563EB' : act.type === 'Like' ? '#DC2626' : act.type === 'Comment' ? '#9333EA' : act.type === 'Share' ? '#0284C7' : '#EA580C',
                              fontWeight: 700
                            }}>
                              {act.type}
                            </span>
                          </td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{act.details}</td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: 12.5 }}>{formatDate(act.created_at)}</td>
                        </tr>
                      ))}

                      {(!data?.recent_activity || data.recent_activity.length === 0) && (
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                            No recent activity recorded for this article.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: LIKES */}
          {activeTab === 'likes' && (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Platform</th>
                      <th>User / Reader</th>
                      <th>Phone Number</th>
                      <th>Device ID / IP</th>
                      <th>Liked On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.rows?.map((row, idx) => (
                      <tr key={row.id}>
                        <td>{(page - 1) * 15 + idx + 1}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600 }}>
                            {getPlatformIcon(row.platform)} {row.platform || 'Android'}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#DC2626' }}>
                              <MdPerson />
                            </div>
                            <span style={{ fontWeight: 600, color: 'var(--text)' }}>{row.user_name}</span>
                          </div>
                        </td>
                        <td>{row.phone}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{row.device_id}</td>
                        <td>{formatDate(row.created_at)}</td>
                      </tr>
                    ))}
                    {data?.rows?.length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                          No likes recorded for this article yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {data?.pages > 1 && (
                <div style={{ padding: 16, borderTop: '1px solid #E2E8F0' }}>
                  <Pagination pagination={{ page, pages: data.pages }} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
                </div>
              )}
            </div>
          )}

          {/* TAB 3: DISLIKES */}
          {activeTab === 'dislikes' && (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Platform</th>
                      <th>User / Reader</th>
                      <th>Phone Number</th>
                      <th>Device ID / IP</th>
                      <th>Disliked On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.rows?.map((row, idx) => (
                      <tr key={row.id}>
                        <td>{(page - 1) * 15 + idx + 1}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600 }}>
                            {getPlatformIcon(row.platform)} {row.platform || 'Android'}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#FFF7ED', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F97316' }}>
                              <MdPerson />
                            </div>
                            <span style={{ fontWeight: 600, color: 'var(--text)' }}>{row.user_name}</span>
                          </div>
                        </td>
                        <td>{row.phone}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{row.device_id}</td>
                        <td>{formatDate(row.created_at)}</td>
                      </tr>
                    ))}
                    {data?.rows?.length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                          No dislikes recorded for this article.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {data?.pages > 1 && (
                <div style={{ padding: 16, borderTop: '1px solid #E2E8F0' }}>
                  <Pagination pagination={{ page, pages: data.pages }} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
                </div>
              )}
            </div>
          )}

          {/* TAB 4: COMMENTS */}
          {activeTab === 'comments' && (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>User</th>
                      <th>Comment Content</th>
                      <th>Likes</th>
                      <th>Comment Date</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.rows?.map((c, idx) => (
                      <tr key={c.id}>
                        <td>{(page - 1) * 15 + idx + 1}</td>
                        <td>
                          <div style={{ fontWeight: 600, color: 'var(--text)' }}>{c.user_name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.phone}</div>
                        </td>
                        <td style={{ maxWidth: 360, lineHeight: 1.5, color: 'var(--text)' }}>
                          "{c.body}"
                        </td>
                        <td>❤️ {c.like_count}</td>
                        <td>{formatDate(c.created_at)}</td>
                        <td>
                          <span className="badge badge-published">Active</span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteComment(c.id)}
                            title="Delete Comment"
                          >
                            <MdDelete />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {data?.rows?.length === 0 && (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                          No comments found for this article.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {data?.pages > 1 && (
                <div style={{ padding: 16, borderTop: '1px solid #E2E8F0' }}>
                  <Pagination pagination={{ page, pages: data.pages }} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
                </div>
              )}
            </div>
          )}

          {/* TAB 5: VIEWS */}
          {activeTab === 'views' && (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Platform</th>
                      <th>User / Reader</th>
                      <th>Phone Number</th>
                      <th>Device ID / IP</th>
                      <th>Viewed On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.rows?.map((row, idx) => (
                      <tr key={row.id}>
                        <td>{(page - 1) * 15 + idx + 1}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600 }}>
                            {getPlatformIcon(row.platform)} {row.platform || 'Android'}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563EB' }}>
                              <MdPerson />
                            </div>
                            <span style={{ fontWeight: 600, color: 'var(--text)' }}>{row.user_name}</span>
                          </div>
                        </td>
                        <td>{row.phone}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{row.device_id}</td>
                        <td>{formatDate(row.read_at)}</td>
                      </tr>
                    ))}
                    {data?.rows?.length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                          No view logs recorded yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {data?.pages > 1 && (
                <div style={{ padding: 16, borderTop: '1px solid #E2E8F0' }}>
                  <Pagination pagination={{ page, pages: data.pages }} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
                </div>
              )}
            </div>
          )}

          {/* TAB 6: SHARES */}
          {activeTab === 'shares' && (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Member</th>
                      <th>Phone Number</th>
                      <th>City</th>
                      <th>Shared On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.rows?.map((row, idx) => (
                      <tr key={row.id}>
                        <td>{(page - 1) * 15 + idx + 1}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0EA5E9' }}>
                              <MdPerson />
                            </div>
                            <span style={{ fontWeight: 600, color: 'var(--text)' }}>{row.user_name}</span>
                          </div>
                        </td>
                        <td>{row.phone}</td>
                        <td>{row.city || 'N/A'}</td>
                        <td>{formatDate(row.created_at)}</td>
                      </tr>
                    ))}
                    {data?.rows?.length === 0 && (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                          No shares recorded for this article yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {data?.pages > 1 && (
                <div style={{ padding: 16, borderTop: '1px solid #E2E8F0' }}>
                  <Pagination pagination={{ page, pages: data.pages }} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
